/**
 * Dinheiro em inteiros — e nada além disso.
 *
 * Toda quantia do produto é um inteiro na menor unidade da moeda (centavos
 * para BRL e USD, unidades inteiras para JPY). `0.1 + 0.2 !== 0.3` é folclore
 * até o dia em que uma divisão de conta fecha com um centavo a mais e alguém
 * precisa explicar por quê para o grupo inteiro.
 *
 * A consequência que atravessa todo este módulo: **a soma das partes é sempre
 * exatamente igual ao total**. Nunca é aproximadamente igual. Se um algoritmo
 * de divisão não conseguir garantir isso, ele está errado — não "arredondado".
 */

/** Casas decimais por moeda. O padrão é 2; as exceções são explícitas. */
const CASAS_POR_MOEDA: Record<string, number> = {
  JPY: 0,
  KRW: 0,
  VND: 0,
  CLP: 0,
  ISK: 0,
  BHD: 3,
  KWD: 3,
  OMR: 3,
  TND: 3,
  JOD: 3,
};

export function decimalPlaces(currency: string): number {
  return CASAS_POR_MOEDA[currency.toUpperCase()] ?? 2;
}

export function minorUnitsPerUnit(currency: string): number {
  return 10 ** decimalPlaces(currency);
}

export class MoneyError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'MoneyError';
  }
}

export function assertMinorAmount(amount: number, campo = 'valor'): void {
  if (!Number.isInteger(amount)) {
    throw new MoneyError(
      `${campo} precisa ser inteiro na menor unidade da moeda (recebido: ${amount}).`,
    );
  }
  if (!Number.isSafeInteger(amount)) {
    throw new MoneyError(`${campo} fora da faixa segura de inteiros.`);
  }
}

export function sum(values: readonly number[]): number {
  return values.reduce((total, valor) => total + valor, 0);
}
